/**
 * BugReportModal — modal pour l'apprenant qui souhaite signaler un bug
 * ou une incohérence via un bouton dédié dans le sidebar.
 *
 * 11/02/2026 (Axe 1a fiabilité).
 *
 * Fonctionnement :
 *   1. L'utilisateur clique sur « Signaler un bug » (sidebar) → ce modal s'ouvre
 *   2. On capture AUTOMATIQUEMENT en arrière-plan :
 *        - Résumé état jeu (day, phase, treasury, vehicles/drivers count, page URL)
 *        - Screenshot de la fenêtre courante (via html2canvas si dispo, sinon skip)
 *        - Clé licence + user_agent
 *   3. L'utilisateur décrit le bug dans un textarea (obligatoire, min 10 chars)
 *   4. POST vers /api/reports/bug → l'utilisateur reçoit un « short_id » à copier
 *   5. Le formateur voit le rapport dans le dashboard admin
 */
import React, { useState, useEffect, useRef } from 'react';
import { X, Bug, Loader2, CheckCircle2, Copy } from 'lucide-react';
import useGameStore from '../store/gameStore';
import useLicenseStore from '../store/licenseStore';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function summariseGameState() {
  const s = useGameStore.getState();
  return {
    day: s.totalDays || 0,
    game_day: s.day || 0,
    month: s.month || 0,
    year: s.year || 0,
    phase: s.phase || 1,
    treasury: s.treasury || 0,
    reputation: s.reputation || 0,
    company_name: s.company?.name || '',
    company_city: s.company?.city || '',
    difficulty: s.difficulty || 'normal',
    vehicles_count: (s.vehicles || []).length,
    drivers_count: (s.drivers || []).length,
    active_contracts_count: (s.activeContracts || []).length,
    credits_count: (s.credits || []).length,
    inbox_count: (s.inbox || []).length,
    unread_count: (s.inbox || []).filter(m => !m.read).length,
    game_started: !!s.gameStarted,
  };
}

/**
 * Best-effort screenshot capture. Uses html2canvas if the lib is loaded
 * (added to window by index.html or dynamic import). Otherwise returns "".
 * Screenshot NEVER blocks the report submission.
 */
async function captureScreenshotBase64() {
  if (typeof window === 'undefined') return '';
  const html2canvas = window.html2canvas;
  if (!html2canvas) return '';
  try {
    const canvas = await html2canvas(document.body, {
      logging: false,
      // Reduce size for network friendliness (max 1200 wide).
      windowWidth: Math.min(document.body.scrollWidth, 1200),
      scale: 0.5,
    });
    // Compressed JPEG @ 60% quality — smaller than PNG.
    return canvas.toDataURL('image/jpeg', 0.6).replace(/^data:image\/jpeg;base64,/, '');
  } catch (_) {
    return '';
  }
}

export default function BugReportModal({ open, onClose }) {
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { shortId, reportId }
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (open) {
      // Focus after transitions.
      const t = setTimeout(() => textareaRef.current?.focus(), 150);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!open) {
      setMessage('');
      setResult(null);
      setError('');
      setSubmitting(false);
      setCopied(false);
    }
  }, [open]);

  const canSubmit = message.trim().length >= 10 && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      const licenseKey = useLicenseStore.getState().licenseKey || 'UNKNOWN';
      // Capture le screenshot en arrière-plan.
      const screenshotBase64 = await captureScreenshotBase64();
      const res = await fetch(`${BACKEND}/api/reports/bug`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          license_key: licenseKey,
          message: message.trim(),
          game_state_summary: summariseGameState(),
          page_url: window.location.pathname + window.location.search,
          user_agent: navigator.userAgent,
          screenshot_base64: screenshotBase64,
        }),
      });
      if (!res.ok) {
        if (res.status === 429) {
          const data = await res.json().catch(() => null);
          throw new Error(data?.detail || 'Limite de signalements atteinte (5 max sur 3 jours). Réessayez plus tard.');
        }
        throw new Error(`Serveur ${res.status}`);
      }
      const data = await res.json();
      setResult({ shortId: data.short_id, reportId: data.report_id });
    } catch (err) {
      setError(err.message || 'Envoi impossible. Vérifiez votre connexion.');
    } finally {
      setSubmitting(false);
    }
  };

  const copyId = () => {
    if (result?.shortId && navigator.clipboard) {
      navigator.clipboard.writeText(result.shortId).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9998] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="bug-report-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <div className="bg-ink-800 border border-ink-600 rounded-lg max-w-lg w-full shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-ink-600 bg-ink-700/40">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded bg-red-500/15 border border-red-500/30">
              <Bug className="w-4 h-4 text-red-400" />
            </div>
            <h2 className="font-display text-sm font-bold text-foreground tracking-tight uppercase">
              Signaler un bug
            </h2>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="text-slate-500 hover:text-slate-300 disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Fermer"
            data-testid="bug-report-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        {!result ? (
          <div className="p-4 space-y-3">
            <p className="text-xs text-slate-400 leading-relaxed">
              Décrivez ce qui ne va pas — ce que vous faisiez, ce que vous attendiez,
              ce qui s&apos;est passé à la place. Nous capturons automatiquement
              votre état de jeu et une capture d&apos;écran de la page.
            </p>
            <textarea
              ref={textareaRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Ex. : J'ai cliqué sur « Signer contrat » mais rien ne s'est passé. Le contrat semble avoir disparu de la liste."
              className="w-full bg-ink-900/80 border border-ink-600 rounded p-2 text-xs text-slate-200 h-32 resize-none focus:outline-none focus:border-accent"
              maxLength={4000}
              data-testid="bug-report-textarea"
            />
            <div className="flex items-center justify-between text-[10px] text-slate-500">
              <span>{message.length} / 4000 caractères ({message.trim().length >= 10 ? '✓ suffisant' : `${10 - message.trim().length} restant(s) minimum`})</span>
            </div>
            {error && (
              <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded p-2" data-testid="bug-report-error">
                {error}
              </div>
            )}
            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                onClick={onClose}
                disabled={submitting}
                className="text-xs px-3 py-1.5 rounded border border-ink-600 text-slate-400 hover:text-slate-200 hover:bg-ink-700 disabled:opacity-30"
                data-testid="bug-report-cancel"
              >
                Annuler
              </button>
              <button
                onClick={submit}
                disabled={!canSubmit}
                className="text-xs font-bold px-4 py-1.5 rounded bg-accent text-white hover:bg-accent/80 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                data-testid="bug-report-submit"
              >
                {submitting && <Loader2 className="w-3 h-3 animate-spin" />}
                {submitting ? 'Envoi…' : 'Envoyer le rapport'}
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6 space-y-4 text-center" data-testid="bug-report-success">
            <div className="mx-auto w-12 h-12 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-foreground">Merci, votre rapport est bien reçu.</h3>
              <p className="text-xs text-slate-400 mt-1">
                Le formateur pourra le consulter dans son tableau de bord.
              </p>
            </div>
            <div className="bg-ink-900/60 border border-ink-600 rounded p-3 inline-flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Numéro de suivi</span>
              <span className="font-mono text-lg font-bold text-accent tracking-widest" data-testid="bug-report-shortid">
                {result.shortId}
              </span>
              <button
                onClick={copyId}
                className="text-slate-400 hover:text-accent p-1"
                title="Copier"
                data-testid="bug-report-copy-id"
              >
                <Copy className="w-3.5 h-3.5" />
              </button>
              {copied && <span className="text-[10px] text-emerald-400">Copié !</span>}
            </div>
            <button
              onClick={onClose}
              className="text-xs font-bold px-4 py-1.5 rounded bg-accent text-white hover:bg-accent/80"
              data-testid="bug-report-close-success"
            >
              Fermer
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
